import { inject, NgModule } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterModule, Routes } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthGuard } from './auth/helpers';
import { CrmAuthGuard } from './auth/helpers/crm-auth.guard';
import { IframeAbleAuthGuard } from './auth/helpers/iframeable-auth.guard';
import { RedirectGuard } from './auth/helpers/redirect.guard';
import { SessionGuard } from './auth/helpers/session.guard';
import { CrmLoginComponent } from './crm-login/crm-login.component';
import { BulkUploadFeatureHelperService } from './global/bulk-upload-feature.helper.service';
import { otpVerificationGuard } from './guards/otp-verification.guard';
import { AuthLoginV2Component } from './main/pages/authentication/auth-login/auth-login.component';
import { AuthRegisterComponent } from './main/pages/authentication/auth-register/auth-register.component';
import { ConfirmationEmailComponent } from './main/pages/authentication/confirmation-email/confirmation-email.component';
import { EmailVerifyComponent } from './main/pages/authentication/email-verify/email-verify.component';
import { EmailVerifyGuard } from './main/pages/authentication/email-verify/email-verify.guard';
import { EmailVerifyResolver } from './main/pages/authentication/email-verify/email-verify.resolver';
import { ForgotPasswordComponent } from './main/pages/authentication/forgot-password/forgot-password.component';
import { ResetPasswordComponent } from './main/pages/authentication/reset-password/reset-password.component';
import { VerifyOtpComponent } from './main/pages/authentication/verify-otp/verify-otp.component';
import { DashboardComponent } from './main/pages/dashboard/dashboard.component';
import { tokenResolver } from './route-resolvers/token-resolver.service';
import { preloadAllLanguagesResolver } from './main/shared/shared/translations/preload-all-languages.resolver';
import { LocalDynamicLanguageService } from './main/shared/shared/translations/local-dynamic-language.service';
import { DynamicLanguageVariant } from './main/shared/shared/translations/dynamic-language.pipe';
import { TemplateType } from './main/pages/templates/template.dto';
import { TeamInvitationComponent } from './main/pages/team-invitation/team-invitation.component'; // NEW

import { routes as LegalHubRoutes } from './main/pages/legal-hub/legal-hub.routing';
import {
    DocumentPlaybookRunApiService,
    PlaybookRunContextEnum,
    PlaybookRunContextToken,
    RunPlaybookApiServiceToken,
    TemplatePlaybookRunApiService
} from "./main/pages/legal-hub/playbooks/document-playbook-run-api.service";
import { EditTemplatePlaybookListFacadeService } from './main/pages/edit-template/store/legal-hub.facade';
import { IPlaybookListFacadeServiceToken } from './main/pages/legal-hub/playbooks/playbook-list/plyabbok-guideline-run-tile/playbook-list.facade.service';
import { DocumentsRightbarPlaybookListFacadeService } from './main/pages/documents/document-right-sidebar/documents-rightbar-playbook-list.facade.service';
import { OnboardingGuard } from './auth/helpers/onboarding.guard';
import { ConfirmInvitationGuard } from './auth/helpers/confirm-invitation.guard';
import { TeamInvitationGuard } from './auth/helpers/team-invitation.guard';
import { AuthSsoComponent } from './main/pages/authentication/auth-sso/auth-sso.component';


export const routes: Routes = [
    {
        path: '',
        redirectTo: '/dashboard',
        pathMatch: 'full'
    },
    {
        path: 'switchuser',
        canActivate: [RedirectGuard],
        children: []
    },
    {
        path: 'iframeable',
        loadChildren: () => import('./main/pages/iframeable/iframeable-select-template/iframeable.module').then(m => m.IframeableModule),
    },
    {
        path: 'login-via-token',
        canActivate: [RedirectGuard],
        children: [],
        data: { key: 'external_login' }
    },
    {
        path: 'dashboard',
        component: DashboardComponent,
        data: { animation: 'zoomIn' },
        canActivate: [AuthGuard, CrmAuthGuard, IframeAbleAuthGuard]
    },
    {
        path: 'onboarding',
        redirectTo: 'login',
        pathMatch: 'full'
    },
    {
        path: 'onboarding/:token',
        loadChildren: () => import('./main/pages/customer-onboard-flow/customer-onboard-flow.module').then(m => m.CustomerOnboardFlowModule),
        canActivate: [OnboardingGuard]
    },

    {
        path: 'onboarding/team-invitation/:token',
        component: TeamInvitationComponent,
        canActivate: [TeamInvitationGuard]
    },

    {
        path: 'trail-period-completed',
        loadChildren: () => import('./main/pages/trail-period-completed/trail-period-completed.module').then(m => m.TrailPeriodCompletedModule),
    },

    {
        path: 'exports',
        loadChildren: () => import('./main/pages/my-exports/my-exports.module').then(m => m.MyExportsModule),
        data: { animation: 'zoomIn' },
        canActivate: [AuthGuard, CrmAuthGuard, IframeAbleAuthGuard]
    },
    {
        path: 'imports',
        loadChildren: () => import('./main/pages/my-imports/my-imports.module').then(m => m.MyImportsModule),
        data: { animation: 'zoomIn' },
        canActivate: [
            AuthGuard,
            CrmAuthGuard,
            IframeAbleAuthGuard,
            async () => {
                //TODO: since bulk upload feature is gonna to be rebuild, it should not accessible for end users as for now
                const bulkUploadHelper = inject(BulkUploadFeatureHelperService);
                const router = inject(Router);

                return await firstValueFrom(bulkUploadHelper.isBulkUploadEnabled$)
                    ? true
                    : router.navigate(['/']);
            }
        ],
    },
    {
        path: 'templates',
        canActivate: [AuthGuard, CrmAuthGuard, IframeAbleAuthGuard],
        loadChildren: () => import('./main/pages/templates/tamplates.module').then(m => m.TemplatesModule),
        data: { key: TemplateType.REGULAR },
    },
    ...LegalHubRoutes,
    {
        path: 'signable-form',
        canActivate: [AuthGuard, CrmAuthGuard, IframeAbleAuthGuard],
        loadChildren: () => import('./main/pages/signable-eform/signable-eform.module').then(m => m.SignAbleEformsModule),
        data: { key: TemplateType.EFORM },
    },
    {
        path: 'documents',
        canActivate: [AuthGuard, CrmAuthGuard, IframeAbleAuthGuard],
        loadChildren: () => import('./main/pages/documents/documents.module').then(m => m.DocumentsModule),
        providers: [
            {
                provide: PlaybookRunContextToken,
                useValue: PlaybookRunContextEnum.DOCUMENT
            },
            {
                provide: RunPlaybookApiServiceToken,
                useClass: DocumentPlaybookRunApiService
            },
            {
                provide: IPlaybookListFacadeServiceToken,
                useClass: DocumentsRightbarPlaybookListFacadeService
            }

        ]
    },
    {
        path: 'edit-template/:id',
        canActivate: [AuthGuard, CrmAuthGuard, IframeAbleAuthGuard],
        loadChildren: () => import('./main/pages/edit-template/edit-template.module').then(m => m.EditTemplateModule),
        data: { key: 'edit-template', redirectRoute: 'templates' },
        resolve: {
            langs: preloadAllLanguagesResolver
        },
        providers: [
            {
                provide: DynamicLanguageVariant,
                useClass: LocalDynamicLanguageService
            },
            {
                provide: PlaybookRunContextToken,
                useValue: PlaybookRunContextEnum.TEMPLATE
            },
            {
                provide: RunPlaybookApiServiceToken,
                useExisting: TemplatePlaybookRunApiService
            },
            {
                provide: IPlaybookListFacadeServiceToken,
                useClass: EditTemplatePlaybookListFacadeService
            }
        ]
    },
    {
        path: 'send-document/:id',
        canActivate: [AuthGuard],
        loadChildren: () => import('./main/pages/edit-template/edit-template.module').then(m => m.EditTemplateModule),
        data: { key: 'send-document', redirectRoute: 'documents' },
        providers: [
            {
                provide: PlaybookRunContextToken,
                useValue: PlaybookRunContextEnum.DOCUMENT
            },
            {
                provide: RunPlaybookApiServiceToken,
                useClass: DocumentPlaybookRunApiService
            },
            {
                provide: IPlaybookListFacadeServiceToken,
                useClass: EditTemplatePlaybookListFacadeService
            }
        ]
    },
    {
        path: 'contacts',
        canActivate: [AuthGuard, CrmAuthGuard, IframeAbleAuthGuard],
        loadChildren: () => import('./main/pages/contacts/contacts.module').then(m => m.ContactsModule)
    },
    {
        path: 'settings',
        canActivate: [AuthGuard, CrmAuthGuard, IframeAbleAuthGuard],
        loadChildren: () => import('./main/pages/settings/settings.module').then(m => m.SettingsModule)
    },
    {
        path: 'addons',
        canActivate: [AuthGuard, CrmAuthGuard, IframeAbleAuthGuard],
        loadChildren: () => import('./main/pages/addons/addons.module').then(m => m.AddonsModule)
    },
    {
        path: 'login',
        component: AuthLoginV2Component,
        data: { animation: 'auth' },
        canActivate: [SessionGuard],
        resolve: {
            leftImage: () => 'assets/images/login-text-box.gif',
            imageUrlAlt: () => 'Login V2'
        }
    },
    {
        path: 'verify-otp',
        component: VerifyOtpComponent,
        data: { animation: 'auth' },
        canActivate: [otpVerificationGuard],
        resolve: {
            leftImage: () => 'assets/images/login-text-box.gif',
            imageUrlAlt: () => 'Login V2'
        }
    },
    {
        path: 'crm-login',
        component: CrmLoginComponent,
        data: { animation: 'auth', loginVia: 'crm' },
        canActivate: [SessionGuard]
    },
    {
        path: 'integrations',
        loadChildren: () => import('./main/pages/integrations/integrations.module').then(m => m.IntegrationsModule),
        canActivate: [AuthGuard],
    },
    {
        path: 'register',
        component: AuthRegisterComponent,
        data: { animation: 'auth' },
        canActivate: [SessionGuard],
        resolve: {

            leftImage: () => 'assets/images/login-text-box.gif',
            imageUrlAlt: () => 'Register V2'
        }
    },
    {
        //TODO: Need to change for the version 3 i believe 
        path: 'forgot-password-v2',
        component: ForgotPasswordComponent,
        data: { animation: 'auth' },
        resolve: {
            leftImage: () => 'assets/images/login-text-box.gif',
            imageUrlAlt: () => 'Forgot password'
        }
    },
    {
        path: 'email-confirm',
        component: ConfirmationEmailComponent,
        data: { animation: 'auth' },
        resolve: {
            leftImage: () => 'assets/images/login-text-box.gif',
            imageUrlAlt: () => 'Forgot password'
        }
    },
    {
        path: 'email-verify',
        redirectTo: 'login',
        pathMatch: 'full'
    },
    {
        path: 'email-verify/:token',
        component: EmailVerifyComponent,
        data: { animation: 'auth' },
        canActivate: [EmailVerifyGuard],
        resolve: {
            verificationData: EmailVerifyResolver, // Attach the resolver
            leftImage: () => 'assets/images/login-text-box.gif',
            imageUrlAlt: () => 'Forgot password'
        }
    },
    {
        path: 'reset-password-v3',
        redirectTo: 'login',
        pathMatch: 'full'
    },
    {
        path: 'reset-password-v3/:token',
        component: ResetPasswordComponent,
        data: { animation: 'auth' },
        canActivate: [
            (route: ActivatedRouteSnapshot) => {
                const router = inject(Router);
                const token = route?.params?.token?.trim().length > 0;
                if (token) {
                    return true;
                }
                return router.navigate(['login']);
            }
        ],
        resolve: {
            token: (route) => route.params.token?.trim()
        }
    },
     {
        path: 'sso-login',
        component: AuthSsoComponent,
        data: { animation: 'auth', loginVia: 'sso' },
        canActivate: [SessionGuard],
        resolve: {
            leftImage: () => 'assets/images/login-text-box.gif',
            imageUrlAlt: () => 'Login V2'
        }
    },
    {
        path: 'social-callback',
        loadChildren: () => import('./auth/social-callback/social-callback.module').then(m => m.SocialCallbackModule),
    },
    {
        path: 'confirmInvitation',
        loadChildren: () => import('./auth/confirm-invitation/confirm-invitation.module').then(m => m.ConfirmInvitationModule),
        canActivate: [ConfirmInvitationGuard]
    },
    {
        path: 'contract-control',
        canActivate: [AuthGuard, CrmAuthGuard, IframeAbleAuthGuard],
        loadChildren: () => import('./main/pages/contract-control/contract-control.module').then(m => m.ContractControlModule)
    },
    {
        path: 'users',
        canActivate: [AuthGuard, CrmAuthGuard, IframeAbleAuthGuard],
        loadChildren: () => import('./main/pages/users/users.module').then(m => m.UsersModule)
    },
    {
        path: 'profile-settings',
        canActivate: [AuthGuard, CrmAuthGuard, IframeAbleAuthGuard],
        loadChildren: () => import('./main/pages/profile-settings/profile-settings.module').then(m => m.ProfileSettingsModule)
    },
    {
        path: 's/:id/:token',
        loadChildren: () => import('./main/signing-pages/signingpages.module').then(m => m.SigningPagesModule),
        data: { page: 'signing', is_sender: 0 },
        resolve: {
            token: tokenResolver
        }
    },
    {
        path: 's/:id/:token/:pdfview',
        loadChildren: () => import('./main/signing-pages/signingpages.module').then(m => m.SigningPagesModule),
        data: { page: 'signing', is_sender: 0 },
        resolve: {
            token: tokenResolver
        }
    },
    {
        path: 'sender',
        loadChildren: () => import('./main/pages/document-detail/document-detail.module').then(m => m.DocumentDetailModule),
        canActivate: [AuthGuard],
    },
    {
        path: 'shareable',
        loadChildren: () => import('./main/pages/shareable-document/shareable-document.module').then(m => m.ShareableDocumentModule),
    },
    {
        path: 'download/:id/:token',
        loadChildren: () => import('./main/pages/download/download.module').then(m => m.DownloadModule),
        resolve: {
            token: tokenResolver
        }
    },
    {
        path: 'online/:id/:token',
        loadChildren: () => import('./main/online-contract/onlinecontract.module').then(m => m.OnlineContractModule),
        data: { page: 'signing', is_sender: 0 },
        resolve: {
            token: tokenResolver
        }
    },
    {
        path: 'online/:id/:token/:user_id',
        loadChildren: () => import('./main/online-contract/onlinecontract.module').then(m => m.OnlineContractModule),
        data: { page: 'signing', is_sender: 0 },
        resolve: {
            token: tokenResolver
        }
    },
    {
        path: 'export-pdf-view/:id/:token',
        loadChildren: () => import('./main/pages/edit-template/export-pdf-view/export-pdf-view.module').then(m => m.ExportPdfViewModule),
        data: { page: 'exporting', is_sender: 0 },
        resolve: {
            token: tokenResolver
        }
    },
    {
        path: '**',
        redirectTo: 'error'
    },
    {
        path: 'error',
        loadChildren: () => import('./main/pages/miscellaneous/miscellaneous.module').then(m => m.MiscellaneousModule)
    }
];

@NgModule({
    imports: [
        RouterModule.forRoot(routes, {
            scrollPositionRestoration: 'enabled'
        }),
    ],
    exports: [RouterModule]
})

export class AppRoutingModule { }  